import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import {
    X, Upload as UploadIcon, BookOpen, User,
    HeadphonesIcon, LogOut, Shield, Loader2,
    Crown, Zap, CheckCircle2, Lock, Star,
    AlertCircle, CalendarClock
} from 'lucide-react';
import { useState } from 'react';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

const NavItem = ({
    icon, label, onClick, active, danger
}: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    active?: boolean;
    danger?: boolean;
}) => (
    <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left text-sm font-medium
            ${danger
                ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                : active
                    ? 'bg-brand-primary/15 text-brand-primary border border-brand-primary/20'
                    : 'text-slate-300 hover:text-white hover:bg-slate-900'
            }`}
    >
        <span className={danger ? 'text-red-400' : active ? 'text-brand-primary' : 'text-slate-400'}>
            {icon}
        </span>
        {label}
    </button>
);

/** Calculate days remaining in the 1-year free premium window */
function usePremiumExpiry(creationTime: string | undefined): {
    expiryDate: Date | null;
    daysLeft: number;
    isPremiumActive: boolean;
    progressPct: number;
} {
    if (!creationTime) return { expiryDate: null, daysLeft: 0, isPremiumActive: false, progressPct: 0 };

    const created = new Date(creationTime);
    const expiry = new Date(created);
    expiry.setFullYear(expiry.getFullYear() + 1);

    const now = new Date();
    const msLeft = expiry.getTime() - now.getTime();
    const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
    const totalDays = 365;
    const daysUsed = totalDays - daysLeft;
    const progressPct = Math.min(100, Math.round((daysUsed / totalDays) * 100));
    const isPremiumActive = daysLeft > 0;

    return { expiryDate: expiry, daysLeft, isPremiumActive, progressPct };
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [loggingOut, setLoggingOut] = useState(false);

    const user = auth.currentUser;
    const displayName = user?.displayName || user?.email?.split('@')[0] || 'Member';
    const email = user?.email ?? '';

    const { expiryDate, daysLeft, isPremiumActive, progressPct } = usePremiumExpiry(
        user?.metadata?.creationTime
    );

    const go = (path: string) => { navigate(path); onClose(); };

    const handleSignOut = async () => {
        setLoggingOut(true);
        try {
            await signOut(auth);
            navigate('/login', { replace: true });
        } catch (err) {
            console.error('Sign out failed:', err);
        } finally {
            setLoggingOut(false);
            onClose();
        }
    };

    const isActive = (path: string) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const formatExpiry = (d: Date | null) =>
        d ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                className={`fixed top-0 right-0 h-full w-80 bg-slate-950 border-l border-slate-800 shadow-2xl transform transition-transform duration-300 z-50 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* ── Header: User identity ── */}
                <div className="p-5 border-b border-slate-800 flex-shrink-0">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-brand-primary/20 border border-brand-primary/30 flex items-center justify-center flex-shrink-0">
                                <User className="w-5 h-5 text-brand-primary" />
                            </div>
                            <div className="min-w-0">
                                <p className="font-semibold text-white text-sm truncate">{displayName}</p>
                                <p className="text-xs text-slate-500 truncate">{email}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1.5 hover:bg-slate-800 rounded-lg flex-shrink-0 ml-2">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* ── Scrollable content ── */}
                <div className="flex-1 overflow-y-auto">

                    {/* ── 1. Elite Circle Activation Banner ── */}
                    <div className="relative mx-4 mt-4 rounded-2xl overflow-hidden border border-amber-500/40 shadow-lg shadow-amber-900/20">
                        {/* Gold shimmer background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-950/80 via-yellow-950/60 to-slate-900/80" />
                        <div className="absolute top-0 right-0 w-28 h-28 bg-amber-500/10 blur-3xl rounded-full" />
                        <div className="absolute bottom-0 left-0 w-20 h-20 bg-yellow-400/5 blur-2xl rounded-full" />

                        <div className="relative p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Crown className="w-4 h-4 text-amber-400" />
                                <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">Elite Circle</span>
                            </div>

                            <p className="text-white text-xs leading-relaxed mb-3">
                                <span className="text-amber-300 font-bold">🐻 Welcome!</span> Your purchase of{' '}
                                <span className="italic text-amber-200">"Sleep Like a Bear"</span> has unlocked{' '}
                                <span className="font-bold text-white">1 Full Year of Premium</span>{' '}
                                — completely <span className="text-green-400 font-bold">FREE</span>.
                            </p>

                            <div className="flex items-baseline gap-2 mb-3">
                                <span className="text-slate-500 line-through text-xs">$119.88 value</span>
                                <span className="text-green-400 font-bold text-sm">Gifted ✓</span>
                            </div>

                            {/* Expiry progress */}
                            {isPremiumActive && (
                                <div className="space-y-1.5">
                                    <div className="flex justify-between text-[10px]">
                                        <span className="text-amber-300/70 flex items-center gap-1">
                                            <CalendarClock className="w-3 h-3" />
                                            {daysLeft} days remaining
                                        </span>
                                        <span className="text-slate-500">Expires {formatExpiry(expiryDate)}</span>
                                    </div>
                                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full transition-all"
                                            style={{ width: `${Math.max(2, 100 - progressPct)}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── 2. Plan Comparison ── */}
                    <div className="px-4 pt-4 pb-2 space-y-3">

                        {/* Basic Plan — deliberately dull */}
                        <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-4 opacity-60">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Basic Plan</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-500 border border-slate-700">NOT ACTIVE</span>
                            </div>
                            <ul className="space-y-1.5 mb-3">
                                {[
                                    'Standard Resolution',
                                    '3-File Storage Limit',
                                    'Basic Support',
                                    'Ad-supported experience',
                                ].map(f => (
                                    <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                                        <Lock className="w-3 h-3 text-slate-700 flex-shrink-0" />
                                        <span className="line-through">{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="flex items-center gap-1.5 text-[10px] text-slate-600 bg-slate-800/50 px-3 py-1.5 rounded-lg border border-slate-700/50">
                                <AlertCircle className="w-3 h-3" />
                                Current status: Insufficient for professional libraries.
                            </div>
                        </div>

                        {/* Premium Plan — gold & vibrant */}
                        <div className="relative rounded-xl border border-amber-500/50 overflow-hidden shadow-xl shadow-amber-900/20">
                            {/* Gold gradient background */}
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-950/60 via-yellow-950/40 to-slate-900" />
                            <div className="absolute top-0 right-0 w-24 h-24 bg-amber-400/10 blur-2xl rounded-full" />

                            <div className="relative p-4">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-1.5">
                                        <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                        <span className="text-xs font-bold text-amber-300 uppercase tracking-wider">Premium Experience</span>
                                    </div>
                                    <span className="text-[10px] px-2.5 py-1 rounded-full bg-green-500/20 text-green-400 font-bold border border-green-500/30 animate-pulse">
                                        ● ACTIVE
                                    </span>
                                </div>

                                <div className="flex items-baseline gap-2 mb-4">
                                    <span className="text-slate-500 line-through text-sm">$9.99 / month</span>
                                    <span className="text-green-400 font-bold text-base">Gifted 🎁</span>
                                </div>

                                <ul className="space-y-2">
                                    {[
                                        { icon: <Zap className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />, text: 'Ultra-HD 3D Rendering' },
                                        { icon: <BookOpen className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />, text: 'Unlimited Private Library' },
                                        { icon: <Star className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />, text: 'Early Access to Sleep Research' },
                                        { icon: <HeadphonesIcon className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />, text: '24/7 Priority Support' },
                                        { icon: <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />, text: 'Zero Ads — Ever' },
                                    ].map(({ icon, text }) => (
                                        <li key={text} className="flex items-center gap-2 text-xs text-amber-100/80 font-medium">
                                            {icon} {text}
                                        </li>
                                    ))}
                                </ul>

                                {/* Expiry badge */}
                                {expiryDate && (
                                    <div className="mt-4 text-[10px] text-amber-300/60 border-t border-amber-500/20 pt-3 flex items-center gap-1">
                                        <CalendarClock className="w-3 h-3" />
                                        Premium active until {formatExpiry(expiryDate)} · {daysLeft} days left
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── 3. Navigation ── */}
                    <div className="px-4 pt-2 pb-2">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-widest px-4 mb-3">Navigation</p>
                        <NavItem icon={<UploadIcon className="w-5 h-5" />} label="Upload PDF" onClick={() => go('/')} active={isActive('/')} />
                        <NavItem icon={<BookOpen className="w-5 h-5" />} label="My Library" onClick={() => go('/library')} active={isActive('/library')} />
                        <NavItem icon={<User className="w-5 h-5" />} label="My Account" onClick={() => go('/account')} active={isActive('/account')} />
                        <NavItem icon={<HeadphonesIcon className="w-5 h-5" />} label="Support" onClick={() => go('/contact')} active={isActive('/contact')} />
                    </div>
                </div>

                {/* ── Footer: Logout + trust badge ── */}
                <div className="p-4 border-t border-slate-800 space-y-3 flex-shrink-0">
                    <button
                        onClick={handleSignOut}
                        disabled={loggingOut}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 hover:text-red-300 font-semibold rounded-xl transition-all text-sm disabled:opacity-50"
                    >
                        {loggingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                        {loggingOut ? 'Signing out...' : 'Log Out'}
                    </button>
                    <div className="flex items-center justify-center gap-1.5 text-xs text-slate-600">
                        <Shield className="w-3 h-3" />
                        EMI Flipbook · 256-bit Encrypted
                    </div>
                </div>
            </div>
        </>
    );
}
